import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import type {patientDetailsType} from "../../utils/typesConstant/types.tsx";

export const patientApiSlice = createApi({
    reducerPath: "patientApi",
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_API_URL || "https://mediassist.tinkernxt.com"}/`,
        prepareHeaders: (headers) => {
            headers.set(
                "Authorization",
                `Bearer ${localStorage.getItem("token")}`
            );
            return headers;
        }
    }),
    tagTypes: ["getPatient", "createPatient", "Notifications"],
    endpoints: (builder) => ({
        //to get single patient data
        getSinglePatient: builder.query<{ message: string, data: patientDetailsType }, string>({
            query: (id) => `api/get_patient/${id}/`,
            providesTags: ["getPatient"],
        }),

        //to get multiple patients data
        getPatient: builder.query<patientDetailsType[], { page: number; page_size: number }>({
            query: ({page, page_size}) => ({
                url: `api/getAllPatients/?page=${page}&page_size=${page_size}`,
                method: "GET",
            }),
            providesTags: ["getPatient"],
        }),
        updatePatient: builder.mutation<{ message: string }, { id: string; body: patientDetailsType }>({
            query: ({id, body}) => ({
                url: `api/update_patient/${id}/`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: ["getPatient"]
        }),

        deletePatient: builder.mutation<{ message: string }, string>({
            query: (id) => ({
                url: `api/delete_patient/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: ["getPatient"],
        }),

        getPatientRecords: builder.query<any[], string>({
            query: (patientId) => `api/get_patient_record/${patientId}/`,
            providesTags: ["getPatient"],
        }),

        updatePatientRecord: builder.mutation<
            { message: string },
            { uid: string; [key: string]: any } // request body type
        >({
            query: (data) => ({
                url: "api/update_patient_record/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["getPatient"],
        }),


        getImageUrls: builder.query<{ data: { urls: string[] } }, string>({
            query: (recordId) => `api/get_image_url/${recordId}/`,  // Adjust if URL differs
        }),

        uploadDocument: builder.mutation<
            { message: string },
            { file: File; isExist: boolean; patientId?: string }
        >({
            query: ({file, isExist, patientId}) => {
                const formData = new FormData();
                formData.append("file", file);

                // Send metadata as a JSON string (backend should json.loads this)
                const metadata = {
                    isExist: isExist,
                    patientId: patientId || ""
                };
                formData.append("data", JSON.stringify(metadata)); // 👈 important

                return {
                    url: "ai/receive_document/",
                    method: "POST",
                    body: formData,
                };
            },
            invalidatesTags: ['getPatient', 'Notifications'],
        }),

        getNotifications: builder.query<any, void>({
            query: () => "/users/get_notifications/",
            providesTags: ["Notifications"],
        }),

        markAllSeen: builder.mutation<{ status: string }, void>({
            query: () => ({
                url: "/users/notifications/mark_all_seen/",
                method: "POST",
            }),
            invalidatesTags: ["Notifications"],
        }),

        markNotificationAsRead: builder.mutation<any, string>({
            query: (uid) => ({
                url: `/users/notifications/mark_as_read/${uid}/`,
                method: "POST",
            }),
            invalidatesTags: ["Notifications"],
        }),


    })
});

export const {
    useGetPatientQuery,
    useUpdatePatientMutation,
    useDeletePatientMutation,
    useGetSinglePatientQuery,
    useGetPatientRecordsQuery,
    useUpdatePatientRecordMutation,
    useLazyGetImageUrlsQuery,
    useUploadDocumentMutation,
    useGetNotificationsQuery,
    useMarkAllSeenMutation,
    useMarkNotificationAsReadMutation,
} = patientApiSlice;

